import { localCatalog } from '../data/catalog'
import { policy } from '../data/policies'
import { ApiError } from '../core/api/http'
import { euro } from '../core/format/currency'
import { postponeLimit } from '../core/rules/refillPlan'
import { subscriptionCadences, subscriptionRefill } from '../utils/refill'
import { applySubscriptionAction } from '../core/rules/subscription'
import type { Subscription } from '../core/types/account'
import type { SubscriptionAction } from '../core/types/requests'

const productById = (id: string) => localCatalog.products.find((p) => p.id === id)

/**
 * DEMO — wat de server bij een abonnementsactie bewaakt (LARAVEL.md §10.0), voor de klant én het
 * beheer: geen ritme onder het minimum per doos (422 `interval_too_small`) en geen doos die later
 * komt dan iets op is (422 `postpone_too_late`).
 */
export function assertRefillAction(sub: Subscription | null, action: SubscriptionAction) {
  if (!sub) return
  if (action.type === 'setInterval' && !subscriptionCadences(sub, productById, policy.subscription).includes(action.weeks)) {
    throw new ApiError(
      `Met dat ritme wordt een doos kleiner dan ${euro(policy.subscription.minBoxValue)}. Kies een ander ritme.`,
      422,
      { weeks: 'Te kleine doos.' },
      'interval_too_small',
    )
  }
  // Een blijvende wijziging van de inhoud mag de volgende doos niet onder het minimum brengen.
  // "Deze keer niet" (toggleSkipItem) wel: dan kiest de klant zelf opschuiven of aanvullen.
  if (action.type === 'setQty' || action.type === 'removeItem' || action.type === 'setUsage') {
    const next = applySubscriptionAction(sub, action, policy.subscription)
    const box = next ? subscriptionRefill(next, productById, policy.subscription, 1).boxes[0] : undefined
    const before = subscriptionRefill(sub, productById, policy.subscription, 1).boxes[0]
    const min = policy.subscription.minBoxValue
    if (box && box.lines.length > 0 && box.total < min && (before?.total ?? 0) >= min) {
      throw new ApiError(`Met deze wijziging wordt de volgende doos kleiner dan ${euro(min)}. Voeg iets toe of kies een ander ritme.`, 422, {}, 'box_too_small')
    }
  }
  if (action.type === 'postponeTo') {
    const { items, boxes } = subscriptionRefill(sub, productById, policy.subscription, 1)
    if (boxes[0] && action.date > postponeLimit(items, boxes[0], policy.subscription)) {
      throw new ApiError('Zo laat raakt er al iets op. Kies een eerdere datum.', 422, { date: 'Te laat.' }, 'postpone_too_late')
    }
  }
}
